import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  RACQUET_SPORT_IDS,
  RACQUET_SPORT_LABELS,
  updateAcademySportsSchema,
  type OrganizationSport,
  type RacquetSportId,
} from '@court-sync/shared';
import { Button, Field, FieldLabel, Input } from '@/components/ui';
import { DashboardDataCard } from '@/components/dashboard/DashboardSectionCard';
import { SelectableCard } from '@/components/onboarding/SelectableCard';
import { SportIconTile } from '@/components/sport/SportIcons';
import { organizationService } from '@/services/organization';

interface EditableSportRow {
  key: string;
  sportId: RacquetSportId;
  customLabel: string | null;
  isPrimary: boolean;
  isActive: boolean;
}

interface OrganizationSportsSettingsTabProps {
  organizationId: string;
  disabled?: boolean;
  listTourId?: string;
}

function newSportRow(sportId: RacquetSportId = 'tennis', isPrimary = false): EditableSportRow {
  return {
    key: `new-${Date.now()}-${Math.random()}`,
    sportId,
    customLabel: null,
    isPrimary,
    isActive: true,
  };
}

function toggleSportRow(rows: EditableSportRow[], sportId: RacquetSportId): EditableSportRow[] {
  const existing = rows.find((row) => row.sportId === sportId);
  if (existing) {
    const next = rows.filter((row) => row.sportId !== sportId);
    if (next.length === 0) {
      return [newSportRow('tennis', true)];
    }
    if (existing.isPrimary) {
      next[0] = { ...next[0], isPrimary: true };
    }
    return next;
  }
  return [...rows, newSportRow(sportId, rows.length === 0)];
}

function setPrimarySportRow(rows: EditableSportRow[], sportId: RacquetSportId): EditableSportRow[] {
  return rows.map((row) => ({ ...row, isPrimary: row.sportId === sportId }));
}

function mapSportsToRows(sports: OrganizationSport[]): EditableSportRow[] {
  const active = sports.filter((sport) => sport.isActive);
  if (active.length === 0) {
    return [newSportRow('tennis', true)];
  }
  return active.map((sport) => ({
    key: sport.id,
    sportId: sport.sportId,
    customLabel: sport.customLabel ?? null,
    isPrimary: sport.isPrimary,
    isActive: true,
  }));
}

export function OrganizationSportsSettingsTab({
  organizationId,
  disabled = false,
  listTourId,
}: OrganizationSportsSettingsTabProps) {
  const [rows, setRows] = useState<EditableSportRow[]>([newSportRow('tennis', true)]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadSports = useCallback(async () => {
    setLoading(true);
    try {
      const res = await organizationService.getOrganizationSports(organizationId);
      if (res.success && res.data) {
        setRows(mapSportsToRows(res.data));
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    void loadSports();
  }, [loadSports]);

  const selectedSportIds = useMemo(() => new Set(rows.map((row) => row.sportId)), [rows]);
  const otherSportRow = rows.find((row) => row.sportId === 'other');
  const hasMultipleSports = rows.length > 1;

  const updateOtherLabel = (customLabel: string) => {
    setRows((prev) =>
      prev.map((row) => (row.sportId === 'other' ? { ...row, customLabel } : row))
    );
  };

  const handleSave = async () => {
    const payload = {
      academyId: organizationId,
      sports: rows.map(({ sportId, customLabel, isPrimary, isActive }) => ({
        sportId,
        customLabel,
        isPrimary,
        isActive,
      })),
    };
    const parsed = updateAcademySportsSchema.safeParse(payload);
    if (!parsed.success) {
      toast.error(parsed.error.errors[0]?.message ?? 'Invalid sports configuration');
      return;
    }

    setSaving(true);
    try {
      const res = await organizationService.updateOrganizationSports(
        organizationId,
        parsed.data.sports.map((sport) => ({
          sportId: sport.sportId,
          customLabel: sport.customLabel,
          isPrimary: sport.isPrimary,
          isActive: sport.isActive,
        }))
      );
      if (res.success) {
        toast.success('Sports updated');
        await loadSports();
      } else {
        toast.error(res.error || 'Failed to save sports');
      }
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Failed to save sports');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        id={listTourId}
        className="flex min-h-[120px] items-center justify-center"
        aria-busy="true"
      >
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  return (
    <div id={listTourId} className="space-y-6">
      <DashboardDataCard
        title="Sports offered"
        description="Select every sport your organization runs. Exactly one must be primary."
        contentClassName="px-4 py-5 sm:px-6"
      >
        <div className="space-y-5">
          <div
            className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
            role="group"
            aria-label="Sports offered"
          >
            {RACQUET_SPORT_IDS.map((sportId) => {
              const selected = selectedSportIds.has(sportId);
              const row = rows.find((item) => item.sportId === sportId);
              const isPrimary = row?.isPrimary ?? false;
              const label =
                sportId === 'other' && row?.customLabel?.trim()
                  ? row.customLabel.trim()
                  : RACQUET_SPORT_LABELS[sportId];

              return (
                <div key={sportId} className="space-y-1.5">
                  <SelectableCard
                    id={`org-sport-${sportId}`}
                    title={label}
                    icon={<SportIconTile sportId={sportId} />}
                    iconContainerClassName="contents"
                    badge={isPrimary ? 'Primary' : undefined}
                    selected={selected}
                    disabled={disabled || saving}
                    selectionMode="multiple"
                    onSelect={() => setRows((prev) => toggleSportRow(prev, sportId))}
                  />
                  {selected && hasMultipleSports ? (
                    <button
                      type="button"
                      className="px-1 text-xs font-medium text-primary-600 hover:underline disabled:opacity-50 dark:text-primary-400"
                      disabled={disabled || saving || isPrimary}
                      onClick={() => setRows((prev) => setPrimarySportRow(prev, sportId))}
                    >
                      {isPrimary ? 'Primary sport' : 'Set as primary'}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          {otherSportRow ? (
            <Field>
              <FieldLabel htmlFor="org-other-sport-label">Name for other sport</FieldLabel>
              <Input
                id="org-other-sport-label"
                value={otherSportRow.customLabel ?? ''}
                disabled={disabled || saving}
                onChange={(event) => updateOtherLabel(event.target.value)}
                placeholder="e.g. Racquetball"
                maxLength={80}
              />
            </Field>
          ) : null}

          <div className="flex justify-end border-t border-border pt-4">
            <Button
              type="button"
              onClick={() => void handleSave()}
              disabled={disabled || saving}
            >
              {saving ? 'Saving…' : 'Save sports'}
            </Button>
          </div>
        </div>
      </DashboardDataCard>
    </div>
  );
}
